import DescriptionIcon from "@mui/icons-material/Description";
import LockIcon from "@mui/icons-material/Lock";
import LoginIcon from "@mui/icons-material/Login";
import { Box, Tooltip, Typography, lighten, styled, useTheme } from "@mui/material";
import MuiDialog from "@mui/material/Dialog";

import { cloneElement, useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { getManagementTenantInfo } from "@/api/tenants/actions";
// import { SPTENANTID, SPUSERGUID } from "@/configs";
import { SidebarContext } from "@/contexts/SidebarContext";
import { logger } from "@/lib/logger";
import { User } from "@/models/user";
import { logOutUser } from "@/slices/auth/authThunk";
import { useDispatch } from "@/store";
import { rootReducerName } from "@/store/rootReducer";
import { featureFlags, reloadFeatureFlags } from "@/utils/featureFlags";
import { useTranslation } from "next-i18next";
import Link from "next/link";

interface FooterItemProps {
  icon: React.ReactElement;
  title: string;
  onClick: (e?: any) => void;
  href?: string;
}

const FooterItem = ({ icon, title, onClick, href }: FooterItemProps) => {
  const { menuState } = useContext(SidebarContext);
  const theme = useTheme();

  const itemContent = (
    <Box
      sx={{
        display: "flex",
        justifyContent: menuState ? "start" : "center",
        borderRadius: "10px",
        alignItems: "center",
        gap: "10px",
        padding: theme.spacing(1, 3),
        cursor: "pointer",
        ".menu-text_title": {
          color: theme.sidebar.menuIconColor,
          fontSize: "0.825rem", // Reduced font size for better hierarchy
          fontWeight: 500,
          transition: "all 0.2s"
        },
        ".icon_footer": {
          fontSize: "20px",
          color: theme.sidebar.menuIconColor,
          transition: "all 0.2s"
        },
        "&:hover": {
          backgroundColor: lighten(theme.sidebar.menuItemBgActive as string, 0.2),
          ".menu-text_title": {
            color: theme.sidebar.menuItemColorActive,
            fontWeight: "bold"
          },
          ".icon_footer": {
            color: `${theme.sidebar.menuItemColorActive} !important`
          }
        },
        "&.active": {
          backgroundColor: theme.sidebar.menuItemBgActive,
          ".menu-text_title": {
            color: theme.sidebar.menuItemColorActive,
            fontWeight: "bold"
          },
          ".icon_footer": {
            color: `${theme.sidebar.menuItemColorActive} !important`
          }
        }
      }}
      onClick={onClick}
    >
      {cloneElement(icon, { className: "icon_footer" })}
      {menuState && <Typography className="menu-text_title">{title}</Typography>}
    </Box>
  );

  return (
    <Box sx={{ width: "100%" }}>
      <Tooltip arrow placement="right" title={title} disableHoverListener={menuState}>
        {href ? (
          <Link href={href} style={{ textDecoration: "none", width: "100%" }}>
            {itemContent}
          </Link>
        ) : (
          itemContent
        )}
      </Tooltip>
    </Box>
  );
};

export const SidebarFooter = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const user: User = useSelector((state) => state[rootReducerName.auth].user);
  const { menuState } = useContext(SidebarContext);
  const theme = useTheme();

  const handleLogout = async (e?: any): Promise<void> => {
    if (e) e.preventDefault();
    try {
      await dispatch(logOutUser({}) as any);
    } catch (err) {
      logger.error("err: ", err);
    }
  };

  // State for modals
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [privacyContent, setPrivacyContent] = useState<string | null>(null);
  const [termsContent, setTermsContent] = useState<string | null>(null);

  // State for debug modal
  const [debugOpen, setDebugOpen] = useState(false);
  // State for feature flags display
  const [flags, setFlags] = useState(featureFlags);
  const [tenantIndustry, setTenantIndustry] = useState<string | null>(null);
  const normalizedTenantIndustry = tenantIndustry?.trim() || null;

  useEffect(() => {
    if (!user?.tenantGuid) return;

    getManagementTenantInfo(user.tenantGuid)
      .then((tenantRes: any) => {
        const industryName = tenantRes?.content?.industry_name;
        setTenantIndustry(industryName || "Default");
      })
      .catch((err) => {
        logger.error("Failed to fetch tenant info for footer", { err });
        setTenantIndustry("Default");
      });
    // Re-fetch when onboarding applies/updates industry (tenantGuid stays constant).
  }, [user?.tenantGuid, user?.industry]);

  // When debug modal opens, update flags after a short delay to catch async load
  useEffect(() => {
    if (debugOpen) {
      reloadFeatureFlags().then(() => {
        setFlags({ ...featureFlags });
      });
    }
  }, [debugOpen]);

  // Load HTML content for modals
  const handleOpenPrivacy = () => {
    if (!privacyContent) {
      fetch("/docs/PrivacyPolicy.html")
        .then((res) => res.text())
        .then(setPrivacyContent);
    }
    setPrivacyOpen(true);
  };

  const handleOpenTerms = () => {
    if (!termsContent) {
      fetch("/docs/TermsOfService.html")
        .then((res) => res.text())
        .then(setTermsContent);
    }
    setTermsOpen(true);
  };

  const handleClosePrivacy = () => setPrivacyOpen(false);
  const handleCloseTerms = () => setTermsOpen(false);

  return (
    <SidebarFooterWrapper>
      <FooterItem icon={<LockIcon />} title={t("Privacy Policy")} onClick={handleOpenPrivacy} />

      <FooterItem icon={<DescriptionIcon />} title={t("Terms & Conditions")} onClick={handleOpenTerms} />

      {user && <FooterItem icon={<LoginIcon />} title={t("Log out")} onClick={handleLogout} href="#" />}

      {/* Privacy Policy Modal */}
      <MuiDialog
        open={privacyOpen}
        onClose={handleClosePrivacy}
        aria-labelledby="privacy-policy-title"
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { bgcolor: "background.paper" } }}
      >
        <Box sx={{ p: 3, maxHeight: "70vh", overflowY: "auto" }}>
          <Typography variant="h6" id="privacy-policy-title" gutterBottom>
            {t("Privacy Policy")}
          </Typography>
          {privacyContent ? (
            <Box
              sx={{
                "& *": {
                  color: "text.primary",
                  backgroundColor: "background.paper !important",
                  fontFamily: "inherit",
                  lineHeight: 1.6,
                  margin: 0,
                  padding: 0,
                  boxSizing: "border-box"
                },
                "& h1, & h2, & h3": {
                  color: "primary.main",
                  marginTop: "1.5em",
                  marginBottom: "0.8em"
                },
                "& p, & ul": { marginBottom: "1.2em" },
                "& ul": { paddingLeft: "20px" },
                "& a": {
                  color: "primary.main",
                  textDecoration: "none",
                  "&:hover": { textDecoration: "underline" }
                }
              }}
              dangerouslySetInnerHTML={{
                __html: privacyContent
                  .replace(/<head\b[^>]*>[\s\S]*?<\/head>/i, "")
                  .replace(/<body[^>]*>|<\/body>/gi, "")
              }}
            />
          ) : (
            <Typography>{t("Loading...")}</Typography>
          )}
        </Box>
      </MuiDialog>

      {/* Terms & Conditions Modal */}
      <MuiDialog
        open={termsOpen}
        onClose={handleCloseTerms}
        aria-labelledby="terms-conditions-title"
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { bgcolor: "background.paper" } }}
      >
        <Box sx={{ p: 3, maxHeight: "70vh", overflowY: "auto" }}>
          <Typography variant="h6" id="terms-conditions-title" gutterBottom>
            {t("Terms & Conditions")}
          </Typography>
          {termsContent ? (
            <Box
              sx={{
                "& *": {
                  color: "text.primary",
                  backgroundColor: "background.paper !important",
                  fontFamily: "inherit",
                  lineHeight: 1.6,
                  margin: 0,
                  padding: 0,
                  boxSizing: "border-box"
                },
                "& h1, & h2, & h3": {
                  color: "primary.main",
                  marginTop: "1.5em",
                  marginBottom: "0.8em"
                },
                "& p, & ul": { marginBottom: "1.2em" },
                "& ul": { paddingLeft: "20px" },
                "& a": {
                  color: "primary.main",
                  textDecoration: "none",
                  "&:hover": { textDecoration: "underline" }
                }
              }}
              dangerouslySetInnerHTML={{
                __html: termsContent.replace(/<head\b[^>]*>[\s\S]*?<\/head>/i, "").replace(/<body[^>]*>|<\/body>/gi, "")
              }}
            />
          ) : (
            <Typography>{t("Loading...")}</Typography>
          )}
        </Box>
      </MuiDialog>

      {menuState && (
        <Box px={3} sx={{ textAlign: { xs: "center", sm: "left" }, width: "100%", mt: 1 }}>
          <Typography
            variant="caption"
            sx={{
              color: lighten(theme.sidebar.menuIconColor as string, 0.4),
              fontSize: "0.7rem",
              userSelect: "none",
              display: "block",
              mt: 0.5
            }}
          >
            {t("Template")}:{" "}
            {normalizedTenantIndustry
              ? t(normalizedTenantIndustry, { defaultValue: normalizedTenantIndustry })
              : t("Loading...")}
          </Typography>
        </Box>
      )}

      {/* Debug Status Modal */}
      <MuiDialog
        open={debugOpen}
        onClose={() => setDebugOpen(false)}
        aria-labelledby="debug-status-title"
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { bgcolor: "background.paper" } }}
      >
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" id="debug-status-title" gutterBottom>
            {t("Debug Status")}
          </Typography>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>{t("Feature Flags:")}</strong>
            </Typography>
            <Box component="pre" sx={{ fontSize: 12, whiteSpace: "pre-wrap", mb: 1 }}>
              {JSON.stringify(flags, null, 2)}
            </Box>
          </Box>
          <Box component="pre" sx={{ fontSize: 12, whiteSpace: "pre-wrap" }}>
            {JSON.stringify(
              {
                NODE_ENV: process.env.NODE_ENV,
                APP_STAGE: process.env.APP_STAGE
              },
              null,
              2
            )}
          </Box>
        </Box>
      </MuiDialog>
    </SidebarFooterWrapper>
  );
};

const SidebarFooterWrapper = styled("div")(
  ({ theme }) => `
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: start;
  gap: 4px;
  color: ${theme.sidebar.menuIconColor};
  padding: 8px;
  width: 100%;
  background: ${theme.palette.mode === "dark" ? theme.sidebar.background : theme.general.card.backgroundPrimary};
  `
);
